import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { DateInputIt } from "@/components/DateInputIt";
import { Calculator, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const PlaceCheck = ({ busy, place, onPick }) => <div className="sm:col-span-2 min-h-5 text-sm" aria-live="polite" data-testid="cf-place-status">
  {busy ? <p className="text-muted-foreground flex items-center gap-1.5" data-testid="cf-place-checking"><Loader2 className="w-4 h-4 animate-spin" />Verifico comune e provincia…</p>
    : place?.ok ? <p className="text-teal-500 flex items-center gap-1.5" data-testid="cf-place-ok"><CheckCircle2 className="w-4 h-4" />Luogo corretto: {place.match}</p>
    : place ? <div className="space-y-1.5" data-testid="cf-place-error"><p className="text-amber-500 flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{place.detail}</p>
      {!!place.suggestions?.length && <div className="flex flex-wrap gap-1.5">{place.suggestions.map((s) => <button type="button" key={`${s.comune}-${s.sigla}`} onClick={() => onPick(s)} className="rounded-full border border-border px-2.5 py-0.5 text-xs transition-colors hover:bg-secondary" data-testid={`cf-place-pick-${s.comune}-${s.sigla}`}>{s.comune} ({s.sigla})</button>)}</div>}</div>
    : null}
</div>;

// Mini-form che calcola il Codice Fiscale dai dati anagrafici e lo restituisce via onResult.
export function CodiceFiscaleButton({ onResult, defaultNome = "", defaultCognome = "", size = "sm", variant = "outline", className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ nome: "", cognome: "", sesso: "M", data_nascita: "", comune: "", provincia: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const [place, setPlace] = useState(null), [placeBusy, setPlaceBusy] = useState(false);
  useEffect(() => {
    setPlace(null); setPlaceBusy(false);
    if (!open || f.comune.trim().length < 2) { return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      setPlaceBusy(true);
      api.get("/tools/codice-fiscale/luogo", { params: { comune: f.comune, provincia: f.provincia } })
        .then(({ data }) => { if (!alive) { return; } setPlace(data); if (data.ok && !f.provincia.trim() && data.sigla && data.sigla !== "EE") { setF((s) => ({ ...s, provincia: data.sigla })); } })
        .catch(() => { if (alive) { setPlace(null); } })
        .finally(() => { if (alive) { setPlaceBusy(false); } });
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [open, f.comune, f.provincia]); // eslint-disable-line react-hooks/exhaustive-deps
  const bad = (k) => (place && !place.ok && place.field === k ? "border-amber-500 focus-visible:ring-amber-500" : "");
  const openDialog = () => {
    setF({ nome: defaultNome || "", cognome: defaultCognome || "", sesso: "M", data_nascita: "", comune: "", provincia: "" });
    setOpen(true);
  };
  const calc = async () => {
    if (!f.nome.trim() || !f.cognome.trim() || !f.data_nascita || !f.comune.trim()) {
      toast.error("Compila nome, cognome, data e luogo di nascita"); return;
    }
    if (place && !place.ok) { toast.error(place.detail); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tools/codice-fiscale", { ...f });
      if (data.codice_fiscale) {
        onResult && onResult(data.codice_fiscale);
        toast.success(`Codice Fiscale: ${data.codice_fiscale}${data.comune_match ? ` · ${data.comune_match}` : ""}`);
        setOpen(false);
      } else {
        toast.error(data.detail || "Impossibile calcolare il CF");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel calcolo del Codice Fiscale");
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button type="button" size={size} variant={variant} className={`gap-1 ${className}`} onClick={openDialog} data-testid="cf-calc-open">
        <Calculator className="w-3.5 h-3.5" /> Calcola CF
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cf-calc-dialog">
          <DialogHeader>
            <DialogTitle>Calcola Codice Fiscale</DialogTitle>
            <DialogDescription>Inserisci i dati anagrafici: il codice viene generato automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nome *</Label><Input className="mt-1.5" value={f.nome} onChange={set("nome")} data-testid="cf-nome" /></div>
            <div><Label>Cognome *</Label><Input className="mt-1.5" value={f.cognome} onChange={set("cognome")} data-testid="cf-cognome" /></div>
            <div>
              <Label>Sesso *</Label>
              <Select value={f.sesso} onValueChange={(v) => setF((s) => ({ ...s, sesso: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="cf-sesso"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="M">Maschio</SelectItem><SelectItem value="F">Femmina</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Data di nascita *</Label><DateInputIt className="mt-1.5" value={f.data_nascita} onChange={(v) => setF((s) => ({ ...s, data_nascita: v }))} testid="cf-data" /></div>
            <div><Label>Comune di nascita *</Label><Input className={`mt-1.5 ${bad("comune")}`} value={f.comune} onChange={set("comune")} placeholder="es. Roma (o Stato estero)" data-testid="cf-comune" /></div>
            <div><Label>Provincia (sigla)</Label><Input className={`mt-1.5 uppercase ${bad("provincia")}`} maxLength={2} value={f.provincia} onChange={(e) => setF((s) => ({ ...s, provincia: e.target.value.replace(/[^a-z]/gi, "").toUpperCase() }))} placeholder="es. RM" data-testid="cf-provincia" /></div>
            <PlaceCheck busy={placeBusy} place={place} onPick={(s) => setF((x) => ({ ...x, comune: s.comune, provincia: s.sigla === "EE" ? "" : s.sigla }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cf-cancel">Annulla</Button>
            <Button onClick={calc} disabled={busy || placeBusy || (place && !place.ok)} data-testid="cf-calc-submit">{busy ? "Calcolo…" : "Genera CF"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
